import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WindowService } from 'ngx-unificator/services';
import { UserService } from './user/user.service';
import { SseEventsEnum } from '../enums/sse.enum';
import { PlayerBalanceUpdatedEvent, PlayerBonusChangedEvent, PlayerDepositRequiredEvent, PlayerEventType } from '../types/sse.type';

@Injectable({
  providedIn: 'root',
})
export class ServerSentEventsService {
  private _window = inject(WindowService);
  private _user = inject(UserService);

  public bonusChanged$ = new BehaviorSubject<PlayerBonusChangedEvent>(null);
  public balanceUpdated$ = new BehaviorSubject<PlayerBalanceUpdatedEvent>(null);
  public depositRequired$ = new BehaviorSubject<PlayerDepositRequiredEvent>(null);

  public initSseEvents() {
    this._user.auth$
      .pipe(
        filter((auth) => !!auth),
        switchMap(() => this._connect(this._user.info.id)),
      )
      .subscribe({
        next: (data: PlayerEventType) => {
          this._resolveSseChannel(data);
        },
        error: (err) => console.error('SSE error:', err),
      });
  }

  private _connect(playerId: string) {
    const host = `https://${this._window.nativeWindow.location.host}`;
    const url = `${environment.sse_host}/${playerId}?types=${Object.values(SseEventsEnum).join(',')}`;

    const resolvedHost = !host.includes('localhost')
      ? url
          .replace(environment.sse_host, host + '/api/v1/sse')
          .replace('https://stage.', 'https://stage-platform.')
          .replace('https://www.', 'https://platform.')
      : url;

    const eventSource = new EventSource(resolvedHost, {
      withCredentials: true,
    });

    return new Observable<any>((observer) => {
      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        observer.next(data);
      };

      Object.keys(SseEventsEnum).forEach((event) => {
        eventSource.addEventListener(event, (event: MessageEvent) => {
          observer.next(JSON.parse(event.data));
        });
      });

      eventSource.onerror = (error) => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  private _resolveSseChannel(data: PlayerEventType) {
    switch (data.type) {
      case 'PLAYER_BALANCE_UPDATED':
        this.balanceUpdated$.next(data);
        break;
      case 'PLAYER_BONUS_CHANGED':
        this.bonusChanged$.next(data);
        break;
      case 'PLAYER_DEPOSIT_REQUIRED':
        this.depositRequired$.next(data);
        break;
    }
  }
}
